import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ConfirmDialog } from '../modal/confirm-dialog';
import { StateSwitch, StatusLabel } from '../../motion';
import { spacing } from '../../style-utils/theme';
import toast from '../../utils/toast';
import {
  Pill,
  Tag,
} from '../pages/dashboard/dashboard-units-components/dashboard-units.styled';
import {
  ShiftCard,
  CardHead,
  CardTitle,
  CardLead,
  CardNote,
  Actions,
  PrimaryAction,
  QuietAction,
  ResolvePanel,
  TimeField,
  FieldLabel,
  Mono,
} from './shifts.styled';
import {
  startShift,
  resolveShift,
  ShiftAlreadyOpenError,
} from '../../api/shifts-client';
import {
  endShiftWithHandoff,
  NoOpenShiftForHandoffError,
} from '../../api/handoff-client';
import { Shift } from '../../interfaces/shift-interfaces';
import { useShifts } from './shift-context';
import { useHandoffInbox } from '../iq/handoff-context';
// The handover opens as a LAYER, and the card inside it carries the aurora —
// correct even though it is raised from a NON-AI surface, because the thing it
// renders is a model-written summary. This file's own styled layer stays
// aurora-free (see shifts.styled.tsx's header); the mark travels with the AI
// content, not with the screen it appears on.
import { HandoffModal } from './handoff-modal';
import { HandoffGatePanel } from './handoff-gate-panel';
import { GateDock, GateDockSpacer, GATE_DOCK_QUERY } from './gate-dock';
import { useMediaQuery } from '../hooks/use-media-query';
import { useAuth } from '../../context/auth-provider';
import {
  Clock,
  clockDayTime,
  clockNote,
  clockTime,
  elapsed,
  toZonedInputValue,
  zonedInputToIso,
} from '../../function-utils/building-time';
import { useClock } from '../../context/clock-context';
import { ClockNote } from '../clock-note/clock-note';

/**
 * THE CLOCK CONTROL — the desk's own attendance record, on the main menu.
 *
 * ════════════════ IT IS NOT AN AI SURFACE AND MUST NOT LOOK LIKE ONE ════════
 * lobbero_be/src/CRUD/shifts.js:5-9 states the rule the backend holds itself to:
 * every clock route MUST work with ai_config.enabled = false. This component
 * mirrors that. It never reads the IQ entitlement, never mounts behind it, and
 * carries no aurora — see the header of shifts.styled.tsx. An organisation that
 * has never bought Lobbero IQ still clocks on.
 *
 * ════════════════ THREE STATES, ONE ACTION EACH ════════════════
 *   1. NOT ON SHIFT       → Clock in.
 *   2. ON SHIFT           → since when, for how long, and Clock out & hand over.
 *      ONE button, by owner decision (2026-08-11). The plain clock-out and the
 *      "I forgot to clock out" self-correction both used to sit beside it; the
 *      first was redundant (a handover with nothing to report says so on its
 *      own) and the second was a supervisory act on the desk's own screen. It
 *      now lives in the Shifts tab, behind shift:manage.
 *   3. BLOCKED            → a forgotten clock-out blocks the next clock-in at
 *      that building and is NEVER auto-closed, not by a clock-in and not by a
 *      cron (shifts.js:11-17). That is a blocking state between a concierge and
 *      the start of their shift, at 07:00, with somebody waiting at the desk. So
 *      the 409 carries the offending shift in its body precisely so this card
 *      can resolve it in ONE interaction instead of sending the desk to support.
 *      This is the ONE resolve left here, and it is not attendance editing: it
 *      is the desk unblocking its own next clock-in.
 *
 * ════════════════ THE OPEN LIST IS A LIST ════════════════
 * GET /shifts/open returns EVERY open shift the caller holds, one per building
 * (shifts.js:185-193). Reading [0] would show the wrong building's shift the
 * moment a manager covers two — invisible with a single-building fixture, wrong
 * in production. This filters by the building in context and mentions the rest
 * as information, never as an error.
 */

const COPY = {
  title: 'Your shift',

  checking: 'Checking your shift…',

  offLead: 'You are not clocked in at this building.',
  clockIn: 'Clock in',
  // The busy labels. Each swaps in WITHOUT the button changing width
  // (StatusLabel stacks every label in one cell), so the card never reflows
  // while the request runs.
  clockingIn: 'Clocking in…',
  handingOver: 'Handing over…',
  closing: 'Closing…',

  onPill: 'On shift',
  cancel: 'Cancel',

  blockedTitle: 'There is already an open shift here',
  /* WHERE THE OBSTACLE IS, when it is not here. Migration 082 made the open
     shift PER PERSON rather than per building, so the row blocking this
     clock-in can be at a building the reader is not looking at — and "there is
     already an open shift here" is then simply false, pointing them at the
     wrong desk. The server names it on the 409 (shifts.js) and this is the only
     place that name has anywhere to go. */
  blockedTitleElsewhere: (where: string): string =>
    `You are still clocked in at ${where}`,
  blockedLead:
    'It was never closed, so a new one cannot be started at this building until it is. Give it the time it actually ended and Lobbero will record that the correction was made afterwards.',
  blockedLeadElsewhere:
    'A person is in one place, so that shift has to be closed before this one can start. Give it the time it actually ended and Lobbero will record that the correction was made afterwards.',
  endedAtLabel: 'Ended at',
  closeShift: 'Close that shift',

  elsewhere: 'You also have an open shift at another building.',

  // Never an error. One open shift per building is legal and expected.
  elsewhereNote:
    'That is not a problem — a shift is per building, and one at another building never blocks this one.',

  retroNote: 'Recorded as closed after the fact.',

  startedOk: 'Clocked in',
  resolvedOk: 'Shift closed',
  badTime: 'Enter the time the shift ended.',

  // ── The handover (D-25 as amended by backend migration 076) ──
  handOver: 'Clock out & hand over',
  handOverNote:
    'Ends your shift and writes a summary of it for the desk. Your managers and admins get it straight away, and the next person to clock in here picks it up.',
  handedOk: 'Handed over — signing you out',
  // The model is called inside the request. Saying "your shift is already
  // closed" is the honest reassurance: by the time the vendor call starts the
  // handover exists on disk, so what the reader is waiting for is the wording,
  // not permission to leave.
  working: 'Writing the handover…',
  workingNote:
    'Your shift is already closed. Lobbero is writing the summary, then signing you out — this can take a few seconds.',

  claimedOnClockIn: 'The previous shift left you a handover',
  takeoverTitle: 'The shift you are taking over',
  takeoverDismiss: 'Start my shift',
};

/* THE CARD'S BODY IS ONE BOX WHOSE FILL CHANGES — checking, off, on, blocked —
   and StateSwitch crossfades the fills in place (mode wait, so two never
   stack) instead of the card's contents popping from one to the next. The
   wrapper re-applies the card's column gap inside the switch. The modals stay
   OUTSIDE it, above: the takeover is a layer, not a fill, and it has to stay
   mounted whatever the body is showing — clocking in changes the body in the
   same commit that arms it. No shadow, no border on this wrapper — the e2e reads
   the handover card inside expecting box-shadow none and border-top 0. */
const CardBody = styled(StateSwitch)`
  > div {
    display: flex;
    flex-direction: column;
    gap: ${spacing.sm};
  }
`;


/* WHOSE CLOCK IS A SHIFT? THE BUILDING'S.
 *
 * GET /shifts/open now carries the desk's own IANA zone beside each open shift
 * (WP-18c), because this card and the Shifts tab rendered ONE `started_at` two
 * ways: the tab has always had `properties.timezone` on its rows and uses it,
 * this card had nothing and fell through to whatever zone the reader's browser
 * happens to be in. Same timestamp, two answers, and the answer that moved was
 * the desk's own.
 *
 * Read through a local widening rather than added to `Shift`: that interface is
 * shared by every shift surface and is not this file's to change. OPTIONAL on
 * purpose — a server that predates the field simply omits it and every call
 * below falls back to the reader's clock, which is exactly today's behaviour. */
type OpenShift = Shift & { building_timezone?: string };

const ResolveForm: React.FC<{
  shift: Shift;
  lead: string;
  busy: boolean;
  /** THE TYPIST'S CLOCK — at the desk, the BUILDING's (R-T12, surface first:
   *  an owner or a manager typing here is on the building's clock too). The
   *  times printed AND the time typed are both on it. */
  clock: Clock;
  onSubmit: (endedAtIso: string) => void;
  onCancel?: () => void;
}> = ({ shift, lead, busy, clock, onSubmit, onCancel }) => {
  const tz = clock.zone;
  // Seeded with NOW — on the clock the field is read on — because that is the
  // answer in the overwhelming majority of cases: somebody noticing at 07:00
  // that yesterday is still open usually wants a time they must type, but
  // somebody closing a shift they just ended wants this untouched.
  const [value, setValue] = useState(() => toZonedInputValue(new Date(), tz));
  const nowMax = toZonedInputValue(new Date(), tz);

  return (
    <ResolvePanel>
      <CardLead>{lead}</CardLead>
      <CardNote>
        {/* THE CLOCK IS NAMED because the number beside it is meaningless
            without one: "07:00" is the same four characters at every desk on
            earth. The field below is a datetime-local, which has no zone of its
            own: it is READ AS THE BUILDING'S WALL CLOCK (zonedInputToIso) and
            says so — it used to be read on the device's clock beside a label
            in the building's. */}
        {`Started ${clockDayTime(shift.started_at, tz)} · open for ${elapsed(
          shift.started_at
        )}`}
      </CardNote>
      <FieldLabel>
        {`${COPY.endedAtLabel} (${clockNote(clock)})`}
        <TimeField
          type="datetime-local"
          value={value}
          // The server rejects a future end time outright (shifts.js:236-238):
          // nobody may claim a shift that has not happened yet. The max here is
          // a courtesy that stops the reader discovering that by being refused;
          // it is NOT the enforcement, which stays server-side.
          max={nowMax}
          onChange={e => setValue(e.target.value)}
        />
      </FieldLabel>
      <Actions>
        <PrimaryAction
          type="button"
          disabled={busy}
          onClick={() => {
            const iso = zonedInputToIso(value, tz);
            if (!iso) {
              toast.error(COPY.badTime);

              return;
            }
            onSubmit(iso);
          }}
        >
          <StatusLabel
            state={busy ? 'busy' : 'idle'}
            labels={{ idle: COPY.closeShift, busy: COPY.closing }}
          />
        </PrimaryAction>
        {onCancel ? (
          <QuietAction type="button" disabled={busy} onClick={onCancel}>
            {COPY.cancel}
          </QuietAction>
        ) : null}
      </Actions>
    </ResolvePanel>
  );
};

interface Props {
  buildingId: number;
  buildingName?: string;
}

export const ShiftClock: React.FC<Props> = ({ buildingId, buildingName }) => {
  /* THE HANDOVER ENDS THE SESSION (owner decision, 2026-09-04), so this card
     needs the one logout path the whole app shares — see auth-provider.tsx. It
     releases the push subscription first, which matters most on exactly this
     screen: a shared front-desk tablet whose previous holder would otherwise
     keep receiving visitor and parcel notifications on a device now in front of
     somebody else. */
  const { logout, user } = useAuth();
  // ONE SOURCE OF TRUTH. This card used to own the open-shift list and hand it
  // up by callback, which made it the only thing allowed to ask. It now reads
  // the same state the menu reads, so the two can never disagree about whether
  // this desk is open — see shift-context.tsx.
  const {
    openShifts,
    refresh: refreshShifts,
    /* IN THE CONTEXT, NOT IN THIS COMPONENT, and that is load-bearing: clocking
       in moves this card from the gate to the title column, so React unmounts it
       and mounts a new one. Local state here is set and destroyed in the same
       commit — measured, with the claim already committed in the database and
       the screen showing nothing. See shift-context.tsx. */
    takeoverArmed,
    setTakeoverArmed,
  } = useShifts();
  const [busy, setBusy] = useState(false);
  const [confirmHandOver, setConfirmHandOver] = useState(false);
  // Set ONLY from a 409 body. It is the state where the local view was stale —
  // another tablet at the same desk, or a load that failed — and the server just
  // handed us everything needed to fix it.
  const [blocked, setBlocked] = useState<Shift | null>(null);

  // ── The handover ──────────────────────────────────────────────────────────
  // OUTGOING carries no state at all now. Handing over takes no input (see
  // onHandOver) and produces no screen — it ends the session. All that is left
  // is the failure, which must stay visible on the card.
  const [handoffError, setHandoffError] = useState<string | null>(null);
  // INCOMING: read from the app-wide provider so this card and the IQ chat
  // agree, and so the badge drops on both the moment either one is opened.
  // `refresh` because clocking in can CLAIM the desk's pending handover and the
  // inbox has to be told; `latestFor` because this card shows that desk's last
  // handover — as a summary on the gate, and in full from "Read the last
  // handover" (2026-09-11).
  const {
    refresh,
    latestFor,
    markRead,
    setBuilding: setHandoffBuilding,
    // The gate's panel says "checking" while the read is in flight instead of
    // "no handover yet", and stays silent for a reader who cannot hold one.
    loading: inboxLoading,
    supported: inboxSupported,
  } = useHandoffInbox();
  /* THE DESK THIS CARD IS ABOUT, declared to the provider.
     Without it the provider answers the un-scoped question and the handover the
     takeover layer raises after clocking in could be another building's — a
     statement about one desk under another desk's name. */
  useEffect(() => {
    setHandoffBuilding(Number.isInteger(buildingId) ? buildingId : null);
  }, [buildingId, setHandoffBuilding]);
  /* WHAT THE LAST PERSON LEFT AT THIS DESK. Read through the scoped selector, so
     it is null rather than wrong while a building change is in flight. */
  const deskHandoff = latestFor(Number.isInteger(buildingId) ? buildingId : null);

  /* ══════════ READ IT AGAIN ══════════
     Owner decision, 2026-09-11. The takeover raises the handover once, on
     clock-in, and "Start my shift" dismissed it for good: nothing on the desk
     could reopen it, and the only other handover screen (/iq-handover) is the
     IQ shift report — which tells an account without IQ that it is not
     enabled. The "Handover tile" earlier notes in this file point managers to
     was removed from the menu on 2026-08-13, so a manager had no way to read
     the desk's handover without taking the desk.

     One quiet link on this card, on and off the clock, opening the SAME
     layer the takeover uses as an ordinary dialog. Reading it here stamps
     nothing: read_at is set only by the recipient dismissing the takeover, so
     a second look — or a manager's look — never moves that signal. */
  const [reviewOpen, setReviewOpen] = useState(false);
  // The same record read from three ends; the card picks its sentence from this.
  const reviewViewer: 'recipient' | 'outgoing' | 'observer' = !deskHandoff
    ? 'observer'
    : deskHandoff.can_mark_read
      ? 'recipient'
      : user && deskHandoff.outgoing_user_id === user.id
        ? 'outgoing'
        : 'observer';

  // Kept as a local name so every call site below reads unchanged; the work is
  // now the provider's, and every other reader is refreshed by the same call.
  const load = refreshShifts;

  useEffect(() => {
    setBlocked(null);
    setHandoffError(null);
    load();
  }, [buildingId, load]);

  const here: OpenShift | undefined = (openShifts || []).find(
    s => s.building_id === buildingId
  );
  const elsewhere = (openShifts || []).filter(s => s.building_id !== buildingId);
  // THE DESK'S OWN CLOCK, for every time this card prints. It comes off the open
  // shift because that is the row the server resolved the building from; when
  // there is no open shift here there is nothing to render a time for either,
  // except the blocked path — where the 409's `void load()` has just re-read the
  // list precisely because it was missing that shift.
  //
  // A BUILDING-CLOCK SURFACE (R-T12): useClock answers the building's zone here
  // for whoever is at the desk, and says so when the building has none.
  const clock = useClock({ buildingId, buildingZone: here?.building_timezone });
  const zone = clock.zone;

  const onClockIn = async (): Promise<void> => {
    setBusy(true);
    try {
      const started = await startShift(buildingId);
      toast.success(COPY.startedOk);
      setBlocked(null);
      await load();
      // CLOCKING IN CLAIMED THE DESK'S PENDING HANDOVER. The inbox was loaded
      // before this request and cannot know that a record it was not allowed to
      // see a second ago is now addressed to this reader — the provider only
      // re-reads on mount and on window focus, and neither happens here. So the
      // one moment the state provably changed is the one moment it is re-read.
      if (started.claimed_handoff) {
        /* ARMED BEFORE THE READ, RENDERED AFTER IT.
           The takeover cannot open here — the row is not in the provider yet,
           and `deskHandoff` in this closure is the value from the render that
           produced this click. So the flag is set, `refresh()` fetches the row
           the claim just addressed to this reader, and the layer opens on the
           render where BOTH are true. That is also what makes the entrance read
           as an arrival rather than as a modal that flashed empty first.

           STRICTLY GATED ON `claimed_handoff`. Anything looser hijacks the
           screen of a manager covering an hour, or of the second person to clock
           in on a shared tablet, neither of whom was handed anything. */
        setTakeoverArmed(true);
        await refresh();
      }
    } catch (e) {
      if (e instanceof ShiftAlreadyOpenError && e.openShift) {
        // Copied out of the catch binding before the closures below capture it:
        // a catch parameter is `unknown` and its narrowing does not survive into
        // a callback, so `e.openShift` inside the updater would not compile.
        const conflict = e.openShift;
        // The whole reason the 409 carries a body: straight into the resolve
        // affordance, no second round trip, no dead end.
        setBlocked(conflict);
        // The conflict means our list was MISSING this shift. It used to be
        // patched into a private copy; with one shared source the honest repair
        // is to RE-READ it, so every other reader learns the same thing instead
        // of only this card knowing.
        void load();
      }
      toast.error(e instanceof Error ? e.message : COPY.blockedTitle);
    } finally {
      setBusy(false);
    }
  };

  /* THE HANDOVER. One call ends the shift AND publishes the record.
   *
   * IT TAKES NO INPUT. The picker this used to open is gone (backend migration
   * 076): the handover is addressed to the DESK, the building's managers and
   * admins are told at once, and the next person to clock in claims it. The old
   * shape required a colleague to be selectable at that exact minute and, on a
   * single-concierge building, there never was one — so the picker came back
   * empty and the only way to leave a record was unreachable. Nothing about that
   * failure was recoverable from the UI, which is why it is not a UI fix.
   *
   * It is still not "clock out, then send a summary": the close and the record
   * are one transaction server-side, so a failure leaves the shift OPEN rather
   * than clocking somebody out into a handover that was never made.
   *
   * The await is LONG — the model is called inside the request — and the button
   * stays busy for all of it, with a line saying why. That is honest rather than
   * optimistic: the person is standing there waiting to leave, and a spinner
   * with no explanation reads as a hang. What they are waiting for is only the
   * WORDING; by the time the vendor call starts, the shift is closed and the
   * handover is already on disk. */
  const onHandOver = async (): Promise<void> => {
    setBusy(true);
    setHandoffError(null);
    try {
      await endShiftWithHandoff(buildingId);
      /* ════════ THE SHIFT ENDS, SO THE SESSION ENDS ════════
         Owner decision, 2026-09-04: "I hand it over, I get warned that I will be
         logged out because my shift is over, I get logged out, the next
         concierge logs in and after he clocks in he gets what was handed over."

         THE WARNING IS THE CONFIRM DIALOG, not a second prompt here — asking
         twice for one decision is how a desk learns to press through both. By
         the time this line runs the shift is already closed server-side and the
         handover is on disk, so there is nothing left to confirm.

         WHY SIGNING OUT IS THE RIGHT END, and not just tidiness: this is a
         SHARED FRONT-DESK TABLET. Leaving the outgoing person's session open on
         it means the next concierge clocks in, logs visitors and signs parcels
         out — all of it attributed to somebody who has gone home. The handover
         is the moment the desk changes hands, so it is the moment the session
         has to.

         NO load() AND NO refresh() ON THIS PATH. Both would fire against a
         session that is about to be destroyed; the second of them would land
         after logout() has cleared the cookie and come back 401. There is
         nothing to re-render — the next thing on screen is the sign-in card.

         `busy` is deliberately NOT cleared: the button must not flick back to
         "Clock out & hand over" in the frames between here and the redirect. */
      toast.success(COPY.handedOk);
      await logout();

      return;
    } catch (e) {
      // The server's own sentence is the specific one — "You have no open shift
      // at this building" — so it is shown as-is rather than replaced. It stays
      // ON THE CARD next to the button, instead of only in a toast that has
      // faded by the time they look back at it.
      const msg = e instanceof Error ? e.message : 'Could not hand over.';
      if (e instanceof NoOpenShiftForHandoffError) {
        // Benign disagreement, exactly as on the plain clock-out path: the
        // client thought a shift was open here and it was not. Nothing failed,
        // so it does not get an error pill — the reload below shows the truth.
        toast(msg);
        setHandoffError(null);
      } else {
        setHandoffError(msg);
        toast.error(msg);
      }
      await load();
    } finally {
      setBusy(false);
    }
  };

  const onResolve = async (shift: Shift, endedAtIso: string): Promise<void> => {
    setBusy(true);
    try {
      await resolveShift(shift.id, endedAtIso);
      toast.success(COPY.resolvedOk);
      setBlocked(null);
      await load();
    } catch (e) {
      // The server's own sentence is the specific one ("ended_at must be after
      // the shift started"), so it is shown as-is rather than replaced.
      toast.error(e instanceof Error ? e.message : COPY.closeShift);
    } finally {
      setBusy(false);
    }
  };

  /* The one key the body crossfades on. Mirrors the ternary below exactly. */
  const cardState =
    openShifts === null ? 'checking' : blocked ? 'blocked' : here ? 'on' : 'off';

  /* ── THE GATE'S TWO PIECES, BUILT ONCE ──
     Below `md` both leave the page for the pinned dock (gate-dock.tsx: "make
     sure both clock in and the handover is visible", owner, 2026-09-11); at
     every other width they sit in the card, summary first. One Clock in in the
     DOM either way, so there is never a hidden twin for a screen reader or a
     test to find. */
  const pinGate = useMediaQuery(GATE_DOCK_QUERY);
  const gatePanel = (compact: boolean) =>
    inboxSupported ? (
      <HandoffGatePanel
        compact={compact}
        handoff={deskHandoff}
        loading={inboxLoading}
        buildingName={buildingName}
        onRead={() => setReviewOpen(true)}
      />
    ) : null;
  const clockInPress = (
    <Actions>
      {/* HUGE, because off the clock this is the only thing on the
          screen — the menu is not rendered at all until it is pressed
          (main-menu.tsx's gate). Owner instruction, 2026-08-14. */}
      <PrimaryAction
        $big
        type="button"
        disabled={busy}
        onClick={onClockIn}
        // The phone dock shortens this press on a sideways phone (gate-dock.tsx).
        data-clock-in=""
      >
        <StatusLabel
          state={busy ? 'busy' : 'idle'}
          labels={{ idle: COPY.clockIn, busy: COPY.clockingIn }}
        />
      </PrimaryAction>
    </Actions>
  );

  /* The way back to the desk's last handover — see "READ IT AGAIN" above.
     Rendered in both states of the card, so it is one element, not two. */
  const reviewLink = deskHandoff ? (
    <div>
      <QuietAction type="button" onClick={() => setReviewOpen(true)}>
        Read the last handover
      </QuietAction>
    </div>
  ) : null;

  const elsewhereNote = elsewhere.length ? (
    <>
      <CardNote>{COPY.elsewhere}</CardNote>
      <CardNote>{COPY.elsewhereNote}</CardNote>
    </>
  ) : null;

  /* WHERE THE INBOUND HANDOVER IS READ, as of 2026-09-11.
   *
   * 2026-08-11 took a "Read it" banner off this card so that the main menu's
   * Handover tile was the one door onto the record; the tile itself was removed
   * on 2026-08-13, which left no door at all once the takeover was dismissed.
   * The owner then asked for the desk's handover before clock in, always. So
   * this card is the door again: a clamped summary on the gate
   * (HandoffGatePanel), "Read the last handover" on shift, and both open the
   * same dialog. The claim on clock-in below is unchanged.
   */

  return (
    <ShiftCard>
      <CardHead>
        <CardTitle>{COPY.title}</CardTitle>
        {here ? <Pill tone="success">{COPY.onPill}</Pill> : null}
      </CardHead>

      {/* ── ONE LAYER, AND IT BELONGS TO THE PERSON ARRIVING ─────────────────
          THE RECEIPT IS GONE. Owner decision, 2026-09-04: "there's no need for
          that what you handed over modal."

          It opened automatically the moment a handover was written, showing the
          outgoing person a copy of what they had just sent. That made sense
          while they stayed on the screen afterwards; they do not any more — the
          handover now ends their session, so a document raised half a second
          before a redirect is a flash of text nobody can read, sitting between
          somebody and the door.

          Nothing is lost. The record is on disk, addressed to the desk, and this
          card shows it to anyone entitled to read it — the summary on the gate
          and "Read the last handover" on shift (2026-09-11).
          What the outgoing person needed from this layer — "it was sent" — is
          the toast. */}
      {/* ══════════ THE HANDOVER ARRIVES, FULL SCREEN, ON CLOCK-IN ══════════
          Owner instruction, 2026-08-21: "if a concierge does it he needs to
          clock in and he should receive the handover in his iq on fullscreen
          immediately in an animated fashion smoothly."

          IT IS NOT INSIDE <IqChat>, and that is deliberate rather than a
          shortcut. IqChat deletes itself for an organisation without the Lobbero
          IQ entitlement (it owns the aurora), while POST /shifts/handoff needs
          only shift:clock and degrades to a deterministic floor with no model at
          all. Putting the arrival inside the chat would mean the accounts most
          dependent on the written handover never see it arrive. The card itself
          drops its aurora for a floor record, so nothing here advertises a
          feature the account does not have.

          READ IS STAMPED ON DISMISS, NOT ON MOUNT. 076 keeps `claimed_at` and
          `read_at` as separate supervisory signals — "it reached somebody" and
          "somebody opened it" — and marking read the moment this appears
          collapses them into one, which is precisely the distinction a
          supervisor chasing an unread handover is relying on. */}
      <HandoffModal
        open={takeoverArmed && !!deskHandoff}
        handoff={deskHandoff}
        viewer="recipient"
        buildingName={buildingName}
        title={COPY.takeoverTitle}
        dismissLabel={COPY.takeoverDismiss}
        takeover
        onClose={() => {
          setTakeoverArmed(false);
          if (deskHandoff && !deskHandoff.read_at) {
            void markRead(deskHandoff.id);
          }
        }}
      />

      {/* READ IT AGAIN — the same layer as the takeover, as an ordinary dialog:
          centred, dismissable, and it stamps nothing on close. */}
      <HandoffModal
        open={reviewOpen && !!deskHandoff}
        handoff={deskHandoff}
        viewer={reviewViewer}
        buildingName={buildingName}
        title="The last handover at this desk"
        dismissLabel="Close"
        onClose={() => setReviewOpen(false)}
      />

      <CardBody state={cardState}>
        {openShifts === null ? (
          <CardNote>{COPY.checking}</CardNote>
        ) : blocked ? (
          <>
            <CardLead>
              {blocked.building_id !== buildingId && blocked.building_name
                ? COPY.blockedTitleElsewhere(blocked.building_name)
                : COPY.blockedTitle}
            </CardLead>
            <ResolveForm
              shift={blocked}
              lead={
                blocked.building_id !== buildingId
                  ? COPY.blockedLeadElsewhere
                  : COPY.blockedLead
              }
              busy={busy}
              clock={clock}
              onSubmit={iso => onResolve(blocked, iso)}
            />
          </>
        ) : here ? (
          <>
            <CardLead>
              {buildingName
                ? `On shift at ${buildingName} since ${clockTime(
                    here.started_at,
                    zone
                  )}`
                : `On shift since ${clockTime(here.started_at, zone)}`}
            </CardLead>
            <CardNote>
              {/* THE BUILDING'S CLOCK, NAMED ONCE — the same shape the handover
                  card uses for its window line. Named because this is the number a
                  supervisor later compares against the Shifts tab, which has
                  always rendered it in the building's zone. */}
              <Mono>{`${clockDayTime(here.started_at, zone)} · ${elapsed(
                here.started_at
              )}`}</Mono>{' '}
              <ClockNote clock={clock} inline />
            </CardNote>
            {here.ended_retroactively ? (
              <Tag tone="neutral">{COPY.retroNote}</Tag>
            ) : null}
            {/* ── ONE WAY OFF THE DESK ────────────────────────────────────────
                Owner decision, 2026-08-11: this card carries a single action.
                It used to carry three, and the two that are gone were each a
                second answer to a question the desk should not be asked:

                  "Clock out" (plain)      leaving without a record. It existed
                                           for a manager covering an hour of admin
                                           with nothing to hand over — but the
                                           handover degrades to "nothing was
                                           logged" on its own, so that case needed
                                           no button of its own.
                  "Forgot to clock out?"   self-correcting a stale shift by typing
                                           the time it really ended. Closing a
                                           shift with a time somebody chose is a
                                           SUPERVISORY act, and it now lives with
                                           supervisors: the Shifts tab, gated on
                                           shift:manage.

                The 409 recovery below (`blocked`) is untouched — that is the desk
                unblocking ITS OWN next clock-in, not editing attendance. */}
            <Actions>
              <PrimaryAction
                type="button"
                disabled={busy}
                /* ASKS FIRST. This ends the shift server-side on the first press,
                   and the desk cannot undo it: the plain clock-out and the
                   self-correction both moved behind `shift:manage`, so a mis-tap
                   costs a supervisor's time to repair. Every other irreversible
                   act in the product confirms; this one — the most consequential
                   thing a concierge can press — did not. */
                onClick={() => setConfirmHandOver(true)}
              >
                <StatusLabel
                  state={busy ? 'busy' : 'idle'}
                  labels={{ idle: COPY.handOver, busy: COPY.handingOver }}
                />
              </PrimaryAction>
            </Actions>
            {/* THE WAIT IS EXPLAINED WHERE IT HAPPENS. The model is called inside
                the request, so this button is busy for seconds while somebody
                stands there wanting to leave. The note crossfades rather than
                popping: same box, different fill. */}
            <CardBody state={busy ? 'working' : 'idle'}>
              {busy ? (
                <>
                  <CardLead>{COPY.working}</CardLead>
                  <CardNote>{COPY.workingNote}</CardNote>
                </>
              ) : (
                <CardNote>{COPY.handOverNote}</CardNote>
              )}
            </CardBody>
            {handoffError ? <Pill tone="alert">{handoffError}</Pill> : null}
            {reviewLink}
            {elsewhereNote}
            <ConfirmDialog
              open={confirmHandOver}
              title="Clock out, hand over and sign out?"
              /* NAMES ALL THREE CONSEQUENCES, and the third one is now the
                 surprise: the shift ends, the handover is written and sent, AND
                 THE SESSION ENDS. Owner decision, 2026-09-04 — "I get warned
                 that I will be logged out because my shift is over."

                 THIS IS THE ONLY WARNING, deliberately. A second prompt after
                 the handover has been written would ask for a decision that has
                 already been taken and cannot be undone, and asking twice for
                 one decision is how a desk learns to press through both. The
                 title carries it too: a reader who skims the buttons and not the
                 paragraph still sees the word.

                 "Signed out on this device" rather than "logged out
                 everywhere": logout() ends THIS session, and other devices are
                 untouched. Saying the stronger thing would be false. */
              message="Your shift ends now and a handover is written from everything logged during it, then sent to the managers and to whoever takes over. You will then be signed out on this device, so the next person can clock in as themselves. You cannot reopen the shift yourself."
              primaryLabel="Hand over and sign out"
              primaryVariant="destructive"
              dismissLabel="Stay on shift"
              onPrimary={() => {
                setConfirmHandOver(false);
                void onHandOver();
              }}
              onDismiss={() => setConfirmHandOver(false)}
            />
          </>
        ) : (
          <>
            {/* ══════════ THE HANDOVER AND THE CLOCK-IN ARE ONE SCREEN ══════════
                Owner instruction, 2026-08-21: "all users should see the last
                clocked out shift handover transparently before clocking in on a
                unified screen with a handover + a clock in."

                This is the surface that already fills the whole screen off the
                clock (main-menu.tsx's gate renders nothing else), and the provider
                is ALREADY holding this desk's newest handover at this moment — it
                was simply never rendered here, so the only way to read what the
                last shift left was to open the IQ chat and go looking for it.

                READING IT HERE DOES NOT CONSUME IT. No markRead call: while a
                handover is pending, `can_mark_read` is false server-side anyway
                (only the named recipient may stamp read_at, and nobody is named
                until somebody clocks in and claims it). So a manager can read the
                desk's open handover without taking it away from the concierge who
                comes next, which is exactly what the owner asked for — "if a
                manager logs in and checks whats up he can see the handover for
                that building". */}
            {/* ══════════ OFF THE CLOCK, THE CLOCK IS THE WHOLE SCREEN ══════════
                Owner decision, 2026-09-04: "this should be fullscreen — You are
                not clocked in at this building / Clock in / Start your shift."

                THE HANDOVER IS NOT HERE ANY MORE, and that is a reversal of the
                2026-08-21 shape rather than an oversight, so the reasoning is
                written down.

                That version put the desk's pending handover ABOVE the clock-in
                so it could be read before taking over. In practice it put a
                document — a headline, several sections, their evidence chips and
                a custody line — between a concierge and the only control on the
                screen, and pushed that control below the fold on a phone
                (measured: y=1128 in a 780px viewport). The reader had to scroll
                past a record addressed to nobody yet in order to find the button
                that would address it to them.

                The order is now the one the desk actually works in: clock in
                first, and the handover ARRIVES, full screen, the moment it
                becomes yours — that is the layer above, gated on
                `claimed_handoff`, and it is a better moment for it than this
                one, because by then the record has a recipient and reading it
                means something.

                CONFIRMED FOR MANAGERS TOO (owner, 2026-09-04): one screen for
                everybody.

                AMENDED 2026-09-11 (owner: "before clock in we must neatly
                display the handover for the specific building always"). The
                gate carries the desk's last handover again — as a clamped
                SUMMARY (HandoffGatePanel below), not the document this note
                describes, and the whole record opens from it in a dialog. The
                "Handover tile" managers were once sent to was removed on
                2026-08-13; the panel is where a manager reads the desk's
                handover without taking the desk. */}
            <CardLead>{COPY.offLead}</CardLead>
            {/* ══════════ THE LAST HANDOVER, BEFORE CLOCK IN ══════════
                Owner instruction, 2026-09-11: "before clock in we must neatly
                display the handover for the specific building always". This
                reverses the 2026-09-04 note above in part — and keeps what it
                protected: the panel is a clamped SUMMARY (who, when, state,
                headline or at most three flags), never the document that put
                Clock in at y=1128, and the whole record opens from its button
                in the same dialog "Read the last handover" uses. Reading it
                here stamps nothing. */}
            {pinGate ? (
              <>
                {/* ON A PHONE both leave the flow for the pinned dock, and the
                    spacer keeps the same room at the end of the page. */}
                <GateDockSpacer />
                <GateDock>
                  {gatePanel(true)}
                  {clockInPress}
                </GateDock>
              </>
            ) : (
              <>
                {gatePanel(false)}
                {clockInPress}
              </>
            )}
            {elsewhereNote}
          </>
        )}
      </CardBody>
    </ShiftCard>
  );
};
